import React from 'react'
import Table from '@/components/table/Table'
import Row from '@/components/table/Row'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import AddField from '@/components/addField/AddField'
import ScrollFade from '@/components/scrollFade/ScrollFade'
import PdfSchema from '@/components/pdfSchema/PdfSchema'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { sv } from 'date-fns/locale'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListSubheader from '@mui/material/ListSubheader'
import { format } from 'date-fns'
import {
  useLazyGetSchemasQuery,
  useLazyGetSchemaQuery,
  useLazyAddSchemaQuery,
  useLazyUpdateSchemaQuery,
  useLazyDeleteSchemaQuery,
  useLazyRequestSubmissionQuery,
  ISchemaCreate,
  ISchemaUpdate,
  ISchema,
} from '@/store/apis/schemas'
import { ERole } from '@/store/apis/users'
import { useTranslation } from 'react-i18next'

interface IState {
  selectedPopup?: 'ADD' | 'DELETE' | 'REQUEST_SUBMISSION' | 'CREATE_PDF'
  deleteSchema?: ISchema
  addSchema?: ISchemaCreate
  editSchema?: ISchemaUpdate
  requestSubmissionSchema?: ISchema
  createPdfSchema?: ISchema
}

const Schemas: React.FC = () => {
  const userRole = localStorage.getItem('userRole')
  const { t } = useTranslation()
  const [getSchemasQuery, { data: schemasData, isFetching: isGettingSchemas }] =
    useLazyGetSchemasQuery()
  const [getSchemaQuery, { data: schemaData }] = useLazyGetSchemaQuery()
  const [addSchemaQuery, { isFetching: isAddingSchema }] =
    useLazyAddSchemaQuery()
  const [updateSchemaQuery, { isFetching: isUpdatingSchema }] =
    useLazyUpdateSchemaQuery()
  const [deleteSchemaQuery, { isFetching: isDeletingSchema }] =
    useLazyDeleteSchemaQuery()
  const [requestSubmission, { isFetching: isRequestingSubmission }] =
    useLazyRequestSubmissionQuery()
  const [state, setState] = React.useState<IState>({})

  React.useEffect(() => {
    getSchemasQuery({})
  }, [getSchemasQuery])

  const handleAdd = async (): Promise<void> => {
    if (state.addSchema) {
      await addSchemaQuery(state.addSchema)
      getSchemasQuery({})
      setState({
        ...state,
        addSchema: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDelete = async (id: number): Promise<void> => {
    await deleteSchemaQuery({ id })
    getSchemasQuery({})
    setState({
      ...state,
      selectedPopup: undefined,
      deleteSchema: undefined,
    })
  }

  const handleEdit = async (id: number): Promise<void> => {
    if (state.editSchema) {
      await updateSchemaQuery({ id, ...state.editSchema })
      getSchemasQuery({})
      setState({ ...state, editSchema: undefined })
    }
  }

  const handleRequestSubmission = async (): Promise<void> => {
    if (
      state.requestSubmissionSchema !== undefined &&
      state.requestSubmissionSchema.requestedSubmissionDate
    ) {
      await requestSubmission({
        id: state.requestSubmissionSchema.id,
        requestedSubmissionDate:
          state.requestSubmissionSchema.requestedSubmissionDate,
      })
      getSchemasQuery({})
      setState({
        ...state,
        requestSubmissionSchema: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const {
    selectedPopup,
    requestSubmissionSchema,
    deleteSchema,
    addSchema,
    editSchema,
    createPdfSchema,
  } = state

  const {
    items = [],
    // totalCount = 0,
    // limit = 10,
    // offset = 0,
    // sortKey = 'id',
    // sortDirection = 'ASC',
    // filter = {},
  } = schemasData ? schemasData : {}

  const schemaUsers = schemaData?.users.map(
    (user) => `${user.firstName} ${user.lastName}`,
  )

  const canReuestSubmission = schemaUsers?.length

  return (
    <>
      <Table
        headerLabels={[
          t('headers.name'),
          t('headers.startDate'),
          t('headers.endDate'),
          t('headers.requestedSubmissionDate'),
        ]}
        isLoading={isGettingSchemas}
        isEditing={!!editSchema}
        isAddOpen={selectedPopup === 'ADD'}
        noButtons={userRole === ERole.Attendancer}
        onAddOpen={() =>
          setState({
            ...state,
            selectedPopup: 'ADD',
            addSchema: {
              name: '',
              startDate: format(new Date(), 'yyyy-MM-dd'),
              endDate: format(new Date(), 'yyyy-MM-dd'),
            },
          })
        }
      >
        {items.map((schema, index) => (
          <Row
            key={index}
            navigateTo={`/schemas/${schema.id}/attendance`}
            isLoading={isUpdatingSchema}
            disableButtons={!!editSchema}
            onEditOpen={
              userRole !== ERole.Attendancer
                ? () => setState({ ...state, editSchema: schema })
                : undefined
            }
            onEditSuccess={() => handleEdit(schema.id)}
            onEditAbort={() => setState({ ...state, editSchema: undefined })}
            onDeleteOpen={
              userRole !== ERole.Attendancer
                ? () =>
                    setState({
                      ...state,
                      selectedPopup: 'DELETE',
                      deleteSchema: schema,
                    })
                : undefined
            }
            extraActions={
              userRole !== ERole.Attendancer && [
                {
                  icon: <ForwardToInboxIcon />,
                  disabled: isUpdatingSchema,
                  onClick: () => {
                    getSchemaQuery({ id: schema.id })
                    setState({
                      ...state,
                      selectedPopup: 'REQUEST_SUBMISSION',
                      requestSubmissionSchema: {
                        ...schema,
                        requestedSubmissionDate: format(
                          new Date(),
                          'yyyy-MM-dd',
                        ),
                      },
                    })
                  },
                },
                {
                  icon: <PictureAsPdfIcon />,
                  disabled: isUpdatingSchema,
                  onClick: () => {
                    getSchemaQuery({ id: schema.id })
                    setState({
                      ...state,
                      selectedPopup: 'CREATE_PDF',
                      createPdfSchema: schema,
                    })
                  },
                },
              ]
            }
            columns={{
              normal: [
                schema.name,
                format(new Date(schema.startDate), 'yyyy-MM-dd'),
                format(new Date(schema.endDate), 'yyyy-MM-dd'),
                `${
                  schema.requestedSubmissionDate
                    ? format(
                        new Date(schema.requestedSubmissionDate),
                        'yyyy-MM-dd',
                      )
                    : ''
                }`,
              ],
              edit: editSchema && [
                <AddField
                  value={editSchema.name}
                  required={true}
                  regex={/^$/}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      editSchema: { ...editSchema, name: newValue },
                    })
                  }
                />,
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={sv}
                >
                  <DatePicker
                    displayWeekNumber
                    defaultValue={new Date(editSchema.startDate)}
                    minDate={new Date()}
                    onChange={(value) =>
                      value &&
                      setState({
                        ...state,
                        editSchema: {
                          ...editSchema,
                          startDate: format(value, 'yyyy-MM-dd'),
                        },
                      })
                    }
                  />
                </LocalizationProvider>,
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={sv}
                >
                  <DatePicker
                    displayWeekNumber
                    defaultValue={new Date(editSchema.endDate)}
                    minDate={new Date()}
                    maxDate={
                      requestSubmissionSchema &&
                      new Date(requestSubmissionSchema.endDate)
                    }
                    onChange={(value) =>
                      value &&
                      setState({
                        ...state,
                        editSchema: {
                          ...editSchema,
                          endDate: format(value, 'yyyy-MM-dd'),
                        },
                      })
                    }
                  />
                </LocalizationProvider>,
                `${
                  schema.requestedSubmissionDate
                    ? format(
                        new Date(schema.requestedSubmissionDate),
                        'yyyy-MM-dd',
                      )
                    : ''
                }`,
              ],
            }}
          />
        ))}
        {selectedPopup === 'ADD' && addSchema && (
          <Row
            isLoading={isAddingSchema}
            onAddSuccess={() => handleAdd()}
            onAddAbort={() =>
              setState({
                ...state,
                addSchema: undefined,
                selectedPopup: undefined,
              })
            }
            extraActions={[
              {
                icon: <ForwardToInboxIcon />,
                disabled: isUpdatingSchema,
              },
              {
                icon: <PictureAsPdfIcon />,
                disabled: isUpdatingSchema,
              },
            ]}
            columns={{
              normal: [
                <AddField
                  value={addSchema.name}
                  required={true}
                  regex={/^$/}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      addSchema: { ...addSchema, name: newValue },
                    })
                  }
                />,
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={sv}
                >
                  <DatePicker
                    displayWeekNumber
                    defaultValue={new Date(addSchema.startDate)}
                    onChange={(value) =>
                      value &&
                      setState({
                        ...state,
                        addSchema: {
                          ...addSchema,
                          startDate: format(value, 'yyyy-MM-dd'),
                        },
                      })
                    }
                  />
                </LocalizationProvider>,
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={sv}
                >
                  <DatePicker
                    displayWeekNumber
                    defaultValue={new Date(addSchema.endDate)}
                    maxDate={
                      requestSubmissionSchema &&
                      new Date(requestSubmissionSchema.endDate)
                    }
                    onChange={(value) =>
                      value &&
                      setState({
                        ...state,
                        addSchema: {
                          ...addSchema,
                          endDate: format(value, 'yyyy-MM-dd'),
                        },
                      })
                    }
                  />
                </LocalizationProvider>,
                '',
              ],
            }}
          />
        )}
      </Table>
      <PopupDialog
        isOpen={selectedPopup === 'DELETE'}
        title={
          <>
            {t('popups.removeSchemeTitle')}: <b>{deleteSchema?.name}</b>
          </>
        }
        content={t('popups.removeSchemeDescription')}
        isLoading={isDeletingSchema}
        onClose={() =>
          setState({
            ...state,
            selectedPopup: undefined,
            deleteSchema: undefined,
          })
        }
        onConfirm={() =>
          deleteSchema?.id ? handleDelete(deleteSchema.id) : null
        }
      />
      <PopupDialog
        isOpen={selectedPopup === 'REQUEST_SUBMISSION'}
        title={
          <>
            {t('popups.requestSubmissionSchemeTitle')}:{' '}
            <b>{requestSubmissionSchema?.name}</b>
          </>
        }
        disabled={!requestSubmissionSchema?.requestedSubmissionDate}
        isLoading={isRequestingSubmission}
        content={
          <>
            {canReuestSubmission ? (
              <>
                {t('popups.requestSubmissionSchemeDescription')}
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={sv}
                >
                  <DateCalendar
                    sx={{
                      height: '300px',
                    }}
                    disablePast
                    displayWeekNumber
                    showDaysOutsideCurrentMonth
                    maxDate={
                      requestSubmissionSchema &&
                      new Date(requestSubmissionSchema.endDate)
                    }
                    onChange={(value) =>
                      value &&
                      setState({
                        ...state,
                        requestSubmissionSchema: requestSubmissionSchema && {
                          ...requestSubmissionSchema,
                          requestedSubmissionDate: format(value, 'yyyy-MM-dd'),
                        },
                      })
                    }
                  />
                </LocalizationProvider>
                {t('popups.sendToUserDescription')}:
                <List
                  sx={{
                    width: '100%',
                    maxWidth: 360,
                    bgcolor: 'background.paper',
                    position: 'relative',
                    overflowY: 'auto',
                    maxHeight: 225,
                  }}
                >
                  <ListSubheader>
                    <b>{t('users')}:</b>
                  </ListSubheader>
                  <ScrollFade />
                  {schemaUsers?.map((user, index) => (
                    <ListItem key={index}>{user}</ListItem>
                  ))}
                </List>
              </>
            ) : (
              <>{t('popups.pleaseAddUsers')}</>
            )}
          </>
        }
        onClose={() =>
          setState({
            ...state,
            selectedPopup: undefined,
            requestSubmissionSchema: undefined,
          })
        }
        onConfirm={
          canReuestSubmission
            ? () =>
                requestSubmissionSchema !== undefined
                  ? handleRequestSubmission()
                  : null
            : undefined
        }
      />
      {schemaData && (
        <PopupDialog
          isOpen={selectedPopup === 'CREATE_PDF'}
          title={
            <>
              {t('popups.createPdfTitle')}: <b>{createPdfSchema?.name}</b>
            </>
          }
          content={<PdfSchema schema={schemaData} />}
          disableMaxWidth
          isLoading={false}
          onClose={() =>
            setState({
              ...state,
              selectedPopup: undefined,
              createPdfSchema: undefined,
            })
          }
        />
      )}
    </>
  )
}

export default Schemas
